/**
 * Compute translate (pan) so an element is brought into view.
 * Works in viewport pixel space: check if element is fully inside viewport;
 * if not, move the page by exactly how much the element is off-screen.
 * No zoom change.
 */

export type Rect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export type ScrollIntoViewParams = {
  viewportWidth: number;
  viewportHeight: number;
  translateX: number;
  translateY: number;
  /** Element rect in viewport pixel coordinates (relative to viewport top-left) */
  elementRectInViewport: Rect;
  margin?: number;
};

export type ScrollIntoViewResult = {
  translateX: number;
  translateY: number;
} | null;

/**
 * Returns new translate so that the element is fully inside the viewport, or null if it already is.
 * Uses viewport pixels only (no scale). Subtracts the overflow amount from current translate.
 */
export function getTranslateToScrollIntoView(params: ScrollIntoViewParams): ScrollIntoViewResult {
  const {
    viewportWidth,
    viewportHeight,
    translateX,
    translateY,
    elementRectInViewport,
    margin = 0,
  } = params;

  const { left: elLeft, top: elTop, right: elRight, bottom: elBottom } = elementRectInViewport;

  const inView =
    elLeft >= 0 - margin &&
    elRight <= viewportWidth + margin &&
    elTop >= 0 - margin &&
    elBottom <= viewportHeight + margin;

  if (inView) return null;

  let diffX = 0;
  if (elLeft < 0 - margin) diffX += elLeft;
  else if (elRight > viewportWidth + margin) diffX += elRight - viewportWidth;

  let diffY = 0;
  if (elTop < 0 - margin) diffY += elTop;
  else if (elBottom > viewportHeight + margin) diffY += elBottom - viewportHeight;

  if (diffX === 0 && diffY === 0) return null;

  return {
    translateX: translateX - diffX,
    translateY: translateY - diffY,
  };
}
